import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { removeFromCart, updateCartQuantity } from "../redux/cartSlice";
import ProductImage from "../components/ProductImage";
import { toast } from "react-toastify";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { mapToArabic } from "../utils/mapToArabic";
import styles from "../styles/cart.module.css";
import { ImSpinner2 } from "react-icons/im";
import { BsBag, BsCartCheck, BsInfoCircle } from 'react-icons/bs';
import { FiShoppingCart, FiMinus, FiPlus, FiTrash2, FiChevronLeft } from "react-icons/fi";
import { MdVerifiedUser } from 'react-icons/md';
import { FaAward, FaShippingFast } from 'react-icons/fa';

const itemKey = (item) => `${item.id}-${item.dimension.id}-${item.material}`;

const CartPage = () => {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(() => new Set());
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const items = useSelector((state) => state.cart.items);

  const handleConfirm = () => {
    setConfirming(true);

    if (!user) {
      navigate("/login");
      setConfirming(false);
      return
    }

    if (!user.name || !user.mobile || !user.street) {
      navigate("/account", { state: { fromCart: true } });
      setConfirming(false);
    } else {
      navigate("/checkout", { state: { fromCart: true } });
      setConfirming(false);
    }
  };

  const handleRemove = (item) => {
    confirmAlert({
      message: `حذف ${item.name} من العربة ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: () => {
            // Play the collapse animation, then actually remove the item.
            const key = itemKey(item);
            setRemoving(prev => new Set(prev).add(key));

            setTimeout(() => {
              try {
                dispatch(removeFromCart({dim_id: item.dimension.id, material: item.material}));

                toast.error(<span className="toast">تم حذف اللوحة من العربة</span>);
              } catch (error) {
                toast.error(<span className="toast">لم نتمكن من حذف اللوحة. حاول مرة اخرى</span>);
              }

              setRemoving(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
              });
            }, 300);
          }
        },
        {
          label: 'لا'
        }
      ]
    });
  };

  const handleIncrease = (item) => {
    try {
      dispatch(updateCartQuantity({ quantity: item.quantity + 1, dim_id: item.dimension.id, material: item.material }));
    } catch (error) {
      toast.error(<span className="toast">لم نتمكن من تعديل الكمية. حاول مرة اخرى</span>);
    }
  };

  const handleDecrease = (item) => {
    try {
      dispatch(updateCartQuantity({ quantity: item.quantity - 1, dim_id: item.dimension.id, material: item.material }));
    } catch (error) {
      toast.error(<span className="toast">لم نتمكن من تعديل الكمية. حاول مرة اخرى</span>);
    }
  };

  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const totalPrice = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb}>
        <Link to="/">الرئيسية</Link>
        <FiChevronLeft />
        <span>عربة التسوق</span>
      </nav>

      <header className={styles.header}>
        <h1 className={styles.title}><FiShoppingCart /> عربة التسوق</h1>
        {items.length > 0 && (
          <span className={styles.countChip}>{totalQuantity} {totalQuantity === 1 ? "قطعة" : "قطع"}</span>
        )}
      </header>

      {items.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <FiShoppingCart strokeWidth={1.2} />
          </div>
          <p className={styles.emptyTitle}>عربة التسوق فارغة</p>
          <p className={styles.emptySub}>تصفح لوحاتنا واملأ عربتك الآن</p>
          <button onClick={() => navigate("/products")} className={styles.emptyCta}>
            <BsBag /> ابدأ التسوق
          </button>
        </div>
      ) : (
        <div className={styles.layout}>
          {/* -------- Items -------- */}
          <div className={styles.list}>
            {items.map((item, i) => {
              const key = itemKey(item);
              const lineTotal = (item.price || 0) * (item.quantity || 1);

              return (
                <article
                  key={key}
                  className={removing.has(key) ? `${styles.card} ${styles.removing}` : styles.card}
                  style={{ "--i": i }}
                >
                  <div className={styles.thumb} onClick={() => navigate(`/product/${item.id}`)}>
                    <ProductImage
                      url={item.image_url}
                      alt={item.name}
                      loading="lazy"
                    />
                  </div>

                  <div className={styles.info}>
                    <p className={styles.name} onClick={() => navigate(`/product/${item.id}`)}>
                      {item.name}
                    </p>

                    <div className={styles.chips}>
                      <span className={styles.chip}>{item.dimension.width} × {item.dimension.height} سم</span>
                      <span className={`${styles.chip} ${styles.chipGold}`}>{mapToArabic(item.material).text}</span>
                      {item.multi && <span className={`${styles.chip} ${styles.chipGold}`}>مجموعة لوحات</span>}
                    </div>

                    <p className={styles.unit}>{item.multi ? "سعر المجموعة" : "سعر القطعة"} {item.price} ₪</p>
                  </div>

                  <div className={styles.itemEnd}>
                    <p className={styles.lineTotal}>
                      <span key={lineTotal} className={styles.lineTotalValue}>{lineTotal}</span>
                      {" "}<small>₪</small>
                    </p>

                    <div className={styles.controls}>
                      <div className={styles.qty}>
                        <button
                          onClick={() => handleIncrease(item)}
                          disabled={item.quantity >= 10}
                          aria-label="زيادة الكمية"
                        >
                          <FiPlus />
                        </button>

                        <span key={item.quantity} className={styles.qtyValue}>{item.quantity}</span>

                        <button
                          onClick={() => handleDecrease(item)}
                          disabled={item.quantity <= 1}
                          aria-label="تقليل الكمية"
                        >
                          <FiMinus />
                        </button>
                      </div>

                      <button
                        className={styles.remove}
                        onClick={() => handleRemove(item)}
                        aria-label="حذف من العربة"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* -------- Summary -------- */}
          <aside className={styles.summary}>
            <h2 className={styles.summaryTitle}>ملخص الطلب</h2>

            <div className={styles.summaryRow}>
              <span>عدد اللوحات</span>
              <span>{items.length}</span>
            </div>

            <div className={styles.summaryRow}>
              <span>عدد القطع</span>
              <span>{totalQuantity}</span>
            </div>

            <div className={styles.divider} />

            <div className={styles.totalRow}>
              <span>الإجمالي</span>
              <strong key={totalPrice}>{totalPrice} <small>₪</small></strong>
            </div>

            <p className={styles.tax}><BsInfoCircle /> السعر يشمل الضريبة</p>

            <button
              onClick={handleConfirm}
              disabled={confirming}
              className={styles.confirm}
            >
              {confirming ?
                <ImSpinner2 className={styles.spinner} /> :
                <BsCartCheck />
              } تأكيد الطلب
            </button>

            <button className={styles.continue} onClick={() => navigate("/products")}>
              اكتشف المزيد من اللوحات <FiChevronLeft />
            </button>

            <div className={styles.trust}>
              <span><MdVerifiedUser /> دفع آمن</span>
              <span><FaAward /> ضمان على الجودة</span>
              <span><FaShippingFast /> شحن سريع</span>
            </div>
          </aside>
        </div>
      )}

      {/* -------- Mobile sticky checkout bar -------- */}
      {items.length > 0 && (
        <div className={styles.mobileBar}>
          <div className={styles.mobileBarPrice}>
            <span>الإجمالي</span>
            <strong>{totalPrice} ₪</strong>
          </div>

          <button onClick={handleConfirm} disabled={confirming}>
            {confirming ?
              <ImSpinner2 className={styles.spinner} /> :
              <BsCartCheck />
            } تأكيد الطلب
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
